import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { trackExternalLink, trackEmergencyContact } from '../utils/analytics';

const { FiExternalLink, FiPhone, FiAlert, FiHome, FiMapPin, FiInfo, FiShield } = FiIcons;

const WeatherFooter = () => {
  const currentYear = new Date().getFullYear();
  const emergencyNumbers = [
    { name: 'Iligan DRRMO', number: '(063) 221-4078', description: 'Disaster Risk Reduction & Management Office' },
    { name: 'Emergency Hotline', number: '911', description: 'National Emergency Response' },
    { name: 'PAGASA Northern Mindanao', number: '(088) 856-1443', description: 'Weather Bureau Regional Office' }
  ];

  const handleExternalLink = (url, text, category = 'External Links') => {
    trackExternalLink(url, text, category);
  };

  return (
    <motion.footer 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: 0.8 }} 
      className="bg-white border-t border-gray-200 mt-12" 
    >
      {/* Official Attribution Banner */}
      <div className="bg-slate-50 border-b border-gray-200 py-6">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full p-2 shadow-sm border border-gray-100 flex items-center justify-center">
                <span className="text-blue-800 font-bold text-xs">PAGASA</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Official Data Attribution</h4>
                <p className="text-sm text-gray-600 max-w-md">
                  Weather forecasts, typhoon bulletins, and storm signals are sourced directly from 
                  <strong> PAGASA</strong> (Philippine Atmospheric, Geophysical and Astronomical Services Administration).
                </p>
              </div>
            </div>
            <div className="h-px w-24 bg-gray-200 md:h-12 md:w-px"></div>
            <div className="flex flex-col space-y-2">
              <a 
                href="https://www.pagasa.dost.gov.ph/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center md:justify-start"
              >
                Visit Official PAGASA Website <SafeIcon icon={FiExternalLink} className="ml-1" />
              </a>
              <span className="text-xs text-gray-500">
                Data provided under Republic Act No. 10692
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div>
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiMapPin} className="mr-2 text-blue-600" />
              <h3 className="font-bold text-gray-900 uppercase tracking-wider text-sm">Iligan Weather Tracker</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              The premier real-time weather monitoring system for the City of Majestic Waterfalls. 
              Always cross-reference with official local government announcements.
            </p>
            <div className="flex items-center text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full w-fit">
              <SafeIcon icon={FiShield} className="mr-1.5" /> OFFICIAL MONITORING PARTNER
            </div>
          </div>

          {/* Emergency Numbers */}
          <div className="lg:col-span-2">
            <h3 className="font-bold text-gray-900 mb-6 flex items-center uppercase tracking-wider text-sm">
              <SafeIcon icon={FiPhone} className="mr-2 text-red-600" /> Emergency Hotline Directory
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {emergencyNumbers.map((contact, index) => (
                <div key={index} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:border-blue-200 transition-colors">
                  <div className="font-bold text-sm text-gray-900 mb-1">{contact.name}</div>
                  <div className="text-blue-700 font-mono text-lg font-bold mb-1">{contact.number}</div>
                  <div className="text-xs text-gray-500">{contact.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Official Resources */}
          <div>
            <h3 className="font-bold text-gray-900 mb-6 flex items-center uppercase tracking-wider text-sm">
              <SafeIcon icon={FiInfo} className="mr-2 text-indigo-600" /> Reference Links
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="https://goiligan.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-blue-600 flex items-center group">
                  GoIligan Official Portal <SafeIcon icon={FiExternalLink} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
              <li>
                <a href="https://www.pagasa.dost.gov.ph/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-blue-600 flex items-center group">
                  PAGASA Main Site <SafeIcon icon={FiExternalLink} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
              <li>
                <a href="https://bagong.pagasa.dost.gov.ph/tropical-cyclone/severe-weather-bulletin" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-600 hover:text-blue-600 flex items-center group">
                  Typhoon Bulletins <SafeIcon icon={FiExternalLink} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-sm text-gray-500">
            © {currentYear} Iligan Weather Tracker. Powered by PAGASA and GoIligan.com
          </div>
          <div className="flex items-center space-x-6">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-widest">Data Source: PAGASA-DOST</span>
            <a href="/llms.txt" className="text-xs text-blue-600 hover:underline">AI Documentation</a>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-8 p-4 bg-orange-50 border border-orange-100 rounded-xl">
          <p className="text-[11px] text-orange-800 leading-relaxed text-center">
            <strong>IMPORTANT DISCLAIMER:</strong> This platform is a visualization tool intended to assist the Iligan community. 
            All weather data is sourced from <strong>PAGASA</strong>. In the event of a conflict between this site and official government broadcasts 
            (TV/Radio/Official Social Media), <strong>ALWAYS</strong> follow the official broadcasts and Iligan DRRMO evacuation orders. 
            This application is not a substitute for official government emergency systems.
          </p>
        </div>
      </div>
    </motion.footer>
  );
};

export default WeatherFooter;